// src/components/Stripe/SubscriptionPage.js
import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Container,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Paper,
  Chip,
  useTheme,
  alpha,
  Fade,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PaymentIcon from '@mui/icons-material/Payment';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import API from '../config';

export default function SubscriptionPage() {
  const { authData } = useContext(AuthContext);
  const token = authData?.access_token;
  const navigate = useNavigate();
  const theme = useTheme();

  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');

  // 1) Load current subscription & available plans
  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    setLoading(true);
    Promise.all([
      axios.get(`${API}/api/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      }),
      axios.get(`${API}/api/subscription/plans`, {
        headers: { Authorization: `Bearer ${token}` }
      })
    ])
      .then(([dash, plansResp]) => {
        setSubscription(dash.data.subscription);
        setPlans(plansResp.data.plans || []);
      })
      .catch(err => {
        console.error(err);
        setError('Failed to load subscription data.');
      })
      .finally(() => setLoading(false));
  }, [token, navigate]);

  // Check if direct payment is enabled via environment variable
  const enableDirectPayment = process.env.REACT_APP_ENABLE_DIRECT_PAYMENT === 'true';
  const [paymentMethod, setPaymentMethod] = useState('redirect'); // 'redirect' or 'direct'

  const startCheckout = async priceId => {
    try {
      if (paymentMethod === 'redirect') {
        // Use Stripe Checkout redirect flow
        const { data } = await axios.post(
          `${API}/api/subscription/create-subscription`,
          { priceId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        window.location.href = data.checkoutUrl;
      } else {
        // Use direct payment flow with our custom UI
        navigate(`/payment?price_id=${priceId}`);
      }
    } catch {
      setError('Could not start checkout.');
    }
  };

  const cancelSubscription = async () => {
    try {
      await axios.post(
        `${API}/api/subscription/cancel-subscription`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubscription(prev => ({ ...prev, status: 'canceled' }));
    } catch {
      setError('Failed to cancel.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '50vh' 
      }}>
        <CircularProgress size={48} thickness={4} />
        <Typography variant="h6" sx={{ mt: 3, fontWeight: 500 }}>
          Loading subscription data...
        </Typography>
      </Box>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert 
          severity="error" 
          variant="filled"
          sx={{
            borderRadius: 2,
            boxShadow: 3,
          }}
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
      {/* Page Header */}
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 700,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            textFillColor: 'transparent',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Manage Your Subscription
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          View your current plan details and explore available subscription options
        </Typography>
      </Box>

      <Stack spacing={6}>
        {/* Current Subscription */}
        <Fade in={true} timeout={500}>
          <Paper
            elevation={3}
            sx={{
              p: 4,
              borderRadius: 3,
              background: `linear-gradient(to right, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(
                theme.palette.background.paper,
                0.98
              )})`,
              backdropFilter: 'blur(8px)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': subscription?.status === 'active' ? {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: 6,
                height: '100%',
                backgroundColor: theme.palette.success.main,
              } : {},
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: subscription?.status === 'active' 
                    ? alpha(theme.palette.success.main, 0.1)
                    : alpha(theme.palette.grey[500], 0.1),
                  mr: 2,
                }}
              >
                {subscription?.status === 'active' ? (
                  <CheckCircleIcon sx={{ color: theme.palette.success.main }} />
                ) : (
                  <CancelIcon sx={{ color: theme.palette.grey[500] }} />
                )}
              </Box>
              <Typography variant="h5" fontWeight={600}>
                Current Plan
              </Typography>
              
              {subscription?.status === 'active' && (
                <Chip 
                  label="Active" 
                  color="success" 
                  size="small" 
                  sx={{ ml: 2 }}
                />
              )}
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            {subscription?.status === 'active' ? (
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={4}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Plan
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {subscription.plan}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Status
                    </Typography>
                    <Typography 
                      variant="h6" 
                      fontWeight={600}
                      sx={{ 
                        color: subscription.status === 'active' 
                          ? theme.palette.success.main 
                          : theme.palette.error.main 
                      }}
                    >
                      {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Renewal Date
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {new Date(subscription.current_period_end).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            ) : (
              <Box sx={{ py: 2 }}>
                <Typography variant="h6" color="text.secondary">
                  You have no active subscription
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Choose a plan below to get started
                </Typography>
              </Box>
            )}
            
            {subscription?.status === 'active' && (
              <Box sx={{ mt: 4 }}>
                <Button 
                  color="error" 
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={cancelSubscription}
                  sx={{
                    borderRadius: 2,
                    py: 1,
                    px: 3,
                  }}
                >
                  Cancel Subscription
                </Button>
              </Box>
            )}
          </Paper>
        </Fade>

        {/* Payment Method Selection - Only show if direct payment is enabled */}
        {enableDirectPayment && (
          <Fade in={true} timeout={700}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                borderRadius: 3,
              }}
            >
              <Typography variant="h5" fontWeight={600} gutterBottom>
                Payment Method
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', gap: 3, mt: 3 }}>
                <Card 
                  variant={paymentMethod === 'redirect' ? 'elevation' : 'outlined'}
                  elevation={paymentMethod === 'redirect' ? 3 : 0}
                  sx={{ 
                    flex: 1,
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    transform: paymentMethod === 'redirect' ? 'translateY(-4px)' : 'none',
                    border: paymentMethod === 'redirect' 
                      ? `2px solid ${theme.palette.primary.main}` 
                      : '1px solid rgba(0,0,0,0.12)',
                  }}
                  onClick={() => setPaymentMethod('redirect')}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Box 
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        mx: 'auto',
                        mb: 2,
                      }}
                    >
                      <CreditCardIcon 
                        fontSize="large" 
                        sx={{ color: theme.palette.primary.main }} 
                      />
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      Stripe Checkout
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Secure checkout hosted by Stripe
                    </Typography>
                  </CardContent>
                </Card>
                
                <Card 
                  variant={paymentMethod === 'direct' ? 'elevation' : 'outlined'}
                  elevation={paymentMethod === 'direct' ? 3 : 0}
                  sx={{ 
                    flex: 1,
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    transform: paymentMethod === 'direct' ? 'translateY(-4px)' : 'none',
                    border: paymentMethod === 'direct' 
                      ? `2px solid ${theme.palette.primary.main}` 
                      : '1px solid rgba(0,0,0,0.12)',
                  }}
                  onClick={() => setPaymentMethod('direct')}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Box 
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        mx: 'auto',
                        mb: 2,
                      }}
                    >
                      <PaymentIcon 
                        fontSize="large" 
                        sx={{ color: theme.palette.primary.main }} 
                      />
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      Direct Payment
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Pay directly through our platform
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            </Paper>
          </Fade>
        )}

        {/* Available Plans */}
        <Box>
          <Typography 
            variant="h5" 
            fontWeight={600} 
            gutterBottom
            sx={{ mb: 3 }}
          >
            Available Plans
          </Typography>
          
          <Grid container spacing={3}>
            {plans.map((plan, index) => {
              const price = (plan.unitAmount / 100).toLocaleString('en-US', {
                style: 'currency',
                currency: plan.currency.toUpperCase()
              });
              const isCurrent = subscription?.priceId === plan.priceId && subscription.status === 'active';
              
              return (
                <Grid item xs={12} md={6} key={plan.priceId}>
                  <Fade in={true} timeout={800 + index * 200}>
                    <Card
                      elevation={isCurrent ? 4 : 2}
                      sx={{ 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 3,
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        overflow: 'hidden',
                        border: isCurrent ? `2px solid ${theme.palette.primary.main}` : 'none',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: 8,
                        },
                      }}
                    >
                      {isCurrent && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 16,
                            right: -30,
                            transform: 'rotate(45deg)',
                            bgcolor: theme.palette.primary.main,
                            color: 'white',
                            py: 0.5,
                            px: 4,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            zIndex: 1,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          }}
                        >
                          CURRENT
                        </Box>
                      )}
                      
                      <CardContent sx={{ flexGrow: 1, p: 4 }}>
                        <Typography 
                          variant="h5" 
                          gutterBottom
                          fontWeight={600}
                        >
                          {plan.productName}
                        </Typography>
                        
                        <Typography 
                          variant="body1" 
                          color="text.secondary" 
                          paragraph
                          sx={{ mb: 3, minHeight: 50 }}
                        >
                          {plan.description}
                        </Typography>
                        
                        <Box sx={{ mb: 3 }}>
                          <Typography 
                            variant="h3" 
                            component="div"
                            fontWeight={700}
                            sx={{ 
                              color: theme.palette.primary.main,
                              display: 'flex',
                              alignItems: 'baseline',
                            }}
                          >
                            {price}
                            <Typography 
                              component="span" 
                              variant="h6" 
                              color="text.secondary"
                              sx={{ ml: 1 }}
                            >
                              / {plan.interval}
                            </Typography>
                          </Typography>
                        </Box>
                      </CardContent>
                      
                      <CardActions sx={{ p: 4, pt: 0 }}>
                        {isCurrent ? (
                          <Button 
                            disabled 
                            variant="outlined"
                            fullWidth
                            sx={{ 
                              py: 1.5,
                              borderRadius: 2,
                            }}
                          >
                            Current Plan
                          </Button>
                        ) : (
                          <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            onClick={() => startCheckout(plan.priceId)}
                            sx={{ 
                              py: 1.5,
                              borderRadius: 2,
                              fontWeight: 600,
                            }}
                          >
                            {subscription?.status === 'active' ? 'Change To This Plan' : 'Subscribe'}
                          </Button>
                        )}
                      </CardActions>
                    </Card>
                  </Fade>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Stack>
      
      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Button 
          onClick={() => navigate(-1)}
          variant="outlined"
          sx={{ 
            py: 1.5,
            px: 4,
            borderRadius: 2,
          }}
        >
          Back
        </Button>
      </Box>
    </Container>
  );
}
